/**
 * Shared UI theme — design tokens + reusable inline-style presets.
 *
 * The editor UI historically hand-rolled the same colours, borders, glass-modal
 * shells and buttons as local `style={{…}}` literals in every component (≈557
 * inline styles across 40 files). This module centralises the most-repeated
 * values so components can reference one source of truth instead of duplicating
 * hex codes and layout.
 *
 * Usage:
 *   import { color, ui } from '../ui/theme';
 *   <div style={ui.dialog}> … </div>
 *   <span style={{ color: color.inkFaint }}>label</span>
 *
 * Presets are plain React.CSSProperties so they can be spread and overridden:
 *   <button style={{ ...ui.btn, width: '100%' }} />
 *
 * Player-specific gameplay colours (the dark #070512 highway, per-lane hexes)
 * are intentionally NOT here — they're gameplay-meaningful and live with the
 * player. This theme covers the editor / dashboard chrome.
 */
import type { CSSProperties } from 'react';

// ── Colour tokens ─────────────────────────────────────────────────────────────
// These now point at the CSS custom properties defined in styles/tokens.css, so
// every component that reads them re-tints when the accent theme changes. The
// previous fixed hex values are kept in comments for reference.
/** Foreground / text colours. */
export const color = {
  ink:       'var(--tx-hi)',     // #F0EEFF — primary text
  inkDim:    'var(--tx-mid)',    // #A8A0CC — secondary text
  inkFaint:  'var(--tx-lo)',     // #6B648A — muted labels / icons
  inkGhost:  'var(--tx-faint)',  // #4A4565 — disabled / placeholder

  accent:     'var(--accent)',      // magenta accent / active state
  accentAlt:  'var(--accent-dim)',  // dim end of the brand gradient
  accentSoft: 'var(--accent-soft)', // lighter accent

  success:      'var(--success)',
  info:         'var(--info)',
  warn:         'var(--warn)',
  danger:       'var(--danger)',
  dangerStrong: 'var(--danger-strong)',
} as const;

/** Border colours (accent-tinted hairlines, by emphasis). */
export const border = {
  subtle:  'var(--line-subtle)',
  default: 'var(--line)',
  strong:  'var(--line-strong)',
  accent:  'var(--line-accent)',
} as const;

/** Surface / background fills. */
export const surface = {
  input:   'var(--surface-input)',
  control: 'var(--surface-control)',
  panel:   'var(--surface-panel)',
  overlay: 'var(--surface-overlay)',
} as const;

/** Accent washes for badges / active fills. */
export const tint = {
  accentLow: 'var(--tint-accent-low)',
  accentMed: 'var(--tint-accent-med)',
  dangerLow: 'var(--tint-danger-low)',
} as const;

export const gradient = {
  /** Primary CTA + progress fill. */
  brand: 'var(--grad-brand)',
  /** Cool variant used by sliders / play buttons. */
  cool: 'var(--grad-cool)',
  /** Selected segmented-button fill. */
  activeChip: 'var(--grad-active-chip)',
} as const;

export const radius = { sm: 6, md: 8, lg: 14 } as const;
export const z = { modal: 100 } as const;
export const blur = { panel: 'blur(14px)', scrim: 'blur(4px)' } as const;
export const font = { sans: "'Montserrat', sans-serif" } as const;

// ── Reusable style presets ────────────────────────────────────────────────────
/** Spreadable CSSProperties for the recurring chrome shapes. */
export const ui = {
  /** Full-screen modal scrim (flex-centred). */
  overlay: {
    position: 'fixed', inset: 0, zIndex: z.modal,
    background: surface.overlay, backdropFilter: blur.scrim, WebkitBackdropFilter: blur.scrim,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  } as CSSProperties,

  /** Glass modal / panel shell. */
  dialog: {
    background: surface.panel, border: `1px solid ${border.strong}`, borderRadius: radius.lg,
    boxShadow: '0 18px 52px rgba(0,0,0,0.65)',
    backdropFilter: blur.panel, WebkitBackdropFilter: blur.panel,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: font.sans, color: color.ink,
  } as CSSProperties,

  /** Space-between header row with a bottom divider. */
  dialogHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${border.default}`, flexShrink: 0,
  } as CSSProperties,

  /** Small square ✕ close button. */
  closeBtn: {
    // Circular accent ring + accent X (matches every editor's dialog close).
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%',
    background: 'transparent',
    border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
    color: 'var(--accent)',
    cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0, flexShrink: 0,
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  } as CSSProperties,

  /** Text / number / select field base. */
  input: {
    background: surface.input, border: `1px solid ${border.default}`, borderRadius: radius.sm,
    color: color.ink, fontFamily: font.sans, fontSize: 12, padding: '6px 10px', outline: 'none',
  } as CSSProperties,

  /** Primary call-to-action button. */
  btn: {
    background: gradient.brand, color: '#fff', border: 'none', borderRadius: radius.md,
    fontFamily: font.sans, fontWeight: 700, cursor: 'pointer', padding: '10px 18px',
  } as CSSProperties,

  /** Neutral secondary button. */
  btnSecondary: {
    background: surface.control, color: color.accentSoft, border: `1px solid ${border.strong}`,
    borderRadius: radius.md, fontFamily: font.sans, fontWeight: 600, cursor: 'pointer', padding: '8px 14px',
  } as CSSProperties,

  /** Destructive button. */
  btnDanger: {
    background: tint.dangerLow, color: color.dangerStrong, border: `1px solid ${color.dangerStrong}`,
    borderRadius: radius.md, fontFamily: font.sans, fontWeight: 700, cursor: 'pointer', padding: '8px 14px',
  } as CSSProperties,

  /** Small rounded badge / chip. */
  chip: {
    background: surface.control, color: color.accentSoft, border: `1px solid ${border.default}`,
    borderRadius: radius.sm, fontFamily: font.sans, fontSize: 11, fontWeight: 700, padding: '4px 8px',
  } as CSSProperties,

  /** 10px uppercase faint section heading. */
  sectionLabel: {
    fontSize: 9, fontWeight: 700, letterSpacing: 1.4, color: color.accentSoft,
    textTransform: 'uppercase',
  } as CSSProperties,

  /** Sub-heading under a section title (e.g. "MUSIC VOLUME" under "MIXER").
   *  Deliberately SMALLER and DARKER than the section title so the hierarchy
   *  reads title → subtitle. Use everywhere a secondary label sits inside a
   *  titled group. */
  subLabel: {
    fontSize: 9, fontWeight: 700, letterSpacing: 1, color: color.inkFaint,
    textTransform: 'uppercase',
  } as CSSProperties,
} as const;

/** Segmented / toggle button — active vs inactive fill, border and text. */
export function segmentBtn(active: boolean): CSSProperties {
  return {
    background: active ? gradient.activeChip : 'transparent',
    border: `1px solid ${active ? border.accent : 'transparent'}`,
    color: active ? color.accent : color.inkFaint,
    borderRadius: radius.md, fontFamily: font.sans, fontWeight: 600, cursor: 'pointer',
    padding: '8px 10px', transition: 'all 0.12s ease',
  };
}

/** Tiny flex-row helper (replaces the ubiquitous {display:flex,gap,alignItems} literal). */
export function row(gap = 8): CSSProperties {
  return { display: 'flex', alignItems: 'center', gap };
}

/** Convert a #RRGGBB hex color to an rgba() string with the given alpha. */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * The shared 3D-backdrop, driven by the BACKGROUND theme tokens (--bg3d-b
 * brightness · --bg3d-c chroma · --bg3d-h hue). This is the Flow editor's
 * original soft-glow backdrop — a colored glow concentrated at the top that
 * fades out into a near-black base, so the bg colour DIFFUSES softly instead of
 * flatly tinting the whole screen. Promoted to a shared token so every editor
 * (Synth VR, Flow, Console) renders the backdrop identically.
 */
export const bg3dGradient =
  'radial-gradient(ellipse at top, oklch(calc(0.45 * var(--bg3d-b)) calc(0.19 * var(--bg3d-c)) var(--bg3d-h) / 0.32) 0%, transparent 62%)';

/** Full-screen backdrop layer (drop inside a fixed/absolute backdrop wrapper). */
export const bg3dLayerStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  // Near-black base (faintly bg-hued) with the soft colored glow painted on top.
  backgroundColor: 'oklch(calc(0.055 * var(--bg3d-b)) calc(0.022 * var(--bg3d-c)) var(--bg3d-h))',
  backgroundImage: bg3dGradient,
  pointerEvents: 'none',
};
